import { Chip, Grid, TableCell, TableRow } from '@mui/material';
import { FC, Fragment, Reducer, SyntheticEvent, useCallback, useEffect, useReducer, useState } from 'react';
import { useParams } from 'react-router-dom';
// components
import { Alert } from 'components/common/Alert';
import { LoadingButton } from 'components/common/LoadingButton';
import NoDataFound from 'components/common/NoDataFound';
import TableComponent from 'components/common/TableComponent';
import TableContainer from 'components/common/TableContainer';
import TableLoader from 'components/common/TableLoader';
import TableSearchField from 'components/common/TableSearchField';
import TableTabsComponent from 'components/common/TableTabsComponent';
// constants, reducers, graphql, styles, helper
import {
  FETCHING_INVOICE_TEXT,
  GRAPH_NOT_FOUND_MSG,
  HTTP_STATUS,
  INVOICE_TABS,
  LOADING_TABLE_ROWS,
  VIEW_TEXT,
} from 'constants/index';
import { INVOICE_TABLE_HEADER } from 'constants/tableHeaders';
import {
  InvoicesPayload,
  InvoiceStatus,
  useFindAllInvoicesLazyQuery,
  useFindAllOrgInvoicesLazyQuery,
  useGetInvoiceLazyQuery,
} from 'generated/graphql';
import { isOrgProp } from 'interfaces';
import { InvoiceStatusType } from 'interfaces/TabTypes';
import { formatTimeStamp, getStatusColor } from 'lib/helper';
import { Action, ActionType, initialState, invoicesReducer, State } from 'reducer/InvoicesReducer';
import { textWhiteSpace } from 'styles/commonComponentStyle';

const Invoice: FC<isOrgProp> = ({ isOrg }) => {
  const params = useParams();
  const [state, dispatch] = useReducer<Reducer<State, Action>>(invoicesReducer, initialState);
  const { page, rowsPerPage, count, data, activeTab, search } = state;
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string>('');

  const { id } = params;

  const [findInvoices, { loading, error }] = useFindAllInvoicesLazyQuery({
    onCompleted: (data) => {
      const { findAllInvoices } = data;
      const { response, data: invoiceData, pagination } = findAllInvoices || {};
      const { status } = response || {};
      if (status === HTTP_STATUS.SUCCESS) {
        const { page, totalCount } = pagination || {};
        dispatch({ type: ActionType.SET_PAGE, page: page || 1 });
        dispatch({ type: ActionType.SET_COUNT, count: totalCount || 0 });
        dispatch({ type: ActionType.SET_DATA, data: invoiceData as InvoicesPayload['data'] });
      }
    },
    onError: (error) => {
      if (error?.message !== GRAPH_NOT_FOUND_MSG) {
        Alert.error(error?.message || '');
      }
      resetPage();
    },
  });

  const [findInvoicesByOrgId] = useFindAllOrgInvoicesLazyQuery({
    onCompleted: (data) => {
      const { findAllOrgInvoices } = data;
      const { response, data: invoiceData, pagination } = findAllOrgInvoices || {};
      const { status } = response || {};
      if (status === HTTP_STATUS.SUCCESS) {
        const { page, totalCount } = pagination || {};
        dispatch({ type: ActionType.SET_PAGE, page: page || 1 });
        dispatch({ type: ActionType.SET_COUNT, count: totalCount || 0 });
        dispatch({ type: ActionType.SET_DATA, data: invoiceData as InvoicesPayload['data'] });
      }
    },
    onError: (error) => {
      if (error?.message !== GRAPH_NOT_FOUND_MSG) {
        Alert.error(error?.message || '');
      }
      resetPage();
    },
  });

  const [getInvoice, { loading: invoiceLoading }] = useGetInvoiceLazyQuery({
    onCompleted: (data) => {
      const { getInvoice } = data;
      const { response, hostedInvoiceUrl } = getInvoice || {};
      const { status } = response || {};
      if (status === HTTP_STATUS.SUCCESS) {
        window.open(hostedInvoiceUrl ?? '', '_blank');
      }
    },
  });

  const tabHandler = (_: SyntheticEvent<Element, Event>, value: string) => {
    dispatch({ type: ActionType.SET_ACTIVE_TAB, activeTab: value as InvoiceStatusType });
  };

  const resetPage = () => {
    dispatch({ type: ActionType.SET_PAGE, page: 1 });
    dispatch({ type: ActionType.SET_COUNT, count: 0 });
    dispatch({ type: ActionType.SET_DATA, data: [] });
  };

  const fetchInvoice = useCallback(async () => {
    if (isOrg && id) {
      await findInvoicesByOrgId({
        variables: {
          findInvoicesByOrgIdInput: {
            organizationId: id,
            search,
            ...(activeTab !== 'all' && { status: activeTab as InvoiceStatus }),
            paginationOptions: {
              limit: rowsPerPage,
              page,
            },
          },
        },
      });
    } else {
      await findInvoices({
        variables: {
          findAllInvoicesInput: {
            userId: id,
            search,
            ...(activeTab !== 'all' && { status: activeTab as InvoiceStatus }),
            paginationOptions: {
              limit: rowsPerPage,
              page,
            },
          },
        },
      });
    }
  }, [isOrg, id, findInvoicesByOrgId, search, activeTab, rowsPerPage, page, findInvoices]);

  const onSearch = (val: string) => {
    dispatch({ type: ActionType.SET_PAGE, page: 1 });
    dispatch({ type: ActionType.SET_SEARCH, search: val });
  };

  const onClear = () => {
    dispatch({ type: ActionType.SET_PAGE, page: 1 });
    dispatch({ type: ActionType.SET_SEARCH, search: '' });
  };

  useEffect(() => {
    fetchInvoice();
  }, [fetchInvoice]);

  const noData = Boolean((!loading && data?.length === 0) || error);

  const handleInvoiceClick = (id: string) => {
    setSelectedInvoiceId(id);
    getInvoice({
      variables: {
        getInvoiceInput: { id },
      },
    });
  };

  return (
    <Grid container>
      <Grid item xs={12} md={8} mt={4}>
        <TableContainer>
          <TableTabsComponent tabsList={INVOICE_TABS} activeTab={activeTab} tabHandler={tabHandler} />

          <TableSearchField search={search} onChange={onSearch} onClear={onClear} />

          <TableComponent
            noData={noData}
            page={page - 1}
            count={count}
            rowsPerPage={rowsPerPage}
            tableHeader={INVOICE_TABLE_HEADER}
            setPage={(p: number) => dispatch({ type: ActionType.SET_PAGE, page: p + 1 })}
            setRowsPerPage={(r: number) => dispatch({ type: ActionType.SET_ROWS_PER_PAGE, rowsPerPage: r })}>
            {loading ? (
              <TableLoader columns={4} rows={LOADING_TABLE_ROWS} />
            ) : (
              <Fragment>
                {data?.map((cell) => {
                  const { id, amount, status, invoiceNumber, createdAt } = cell || {};
                  const createdAtDate = formatTimeStamp(createdAt);
                  const statusColor = getStatusColor(status);

                  return (
                    <TableRow key={id}>
                      <TableCell sx={textWhiteSpace}>{invoiceNumber ?? '--'}</TableCell>
                      <TableCell>{`$${amount ?? '0'}`}</TableCell>
                      <TableCell>
                        <Chip size="small" variant="outlined" color={statusColor} label={status ?? '--'} />
                      </TableCell>
                      <TableCell>{createdAtDate ?? '--'}</TableCell>

                      <TableCell>
                        <LoadingButton
                          text={VIEW_TEXT}
                          loading={invoiceLoading && selectedInvoiceId === id}
                          loadingText={FETCHING_INVOICE_TEXT}
                          onClick={() => handleInvoiceClick(id)}
                          appLoader
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </Fragment>
            )}
          </TableComponent>
          <NoDataFound noData={noData} />
        </TableContainer>
      </Grid>
    </Grid>
  );
};

export default Invoice;
